'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

/**
 * RevealInit — arms the sitewide scroll reveals (see "Scroll reveal" in
 * src/styles/tailwind.css).
 *
 * Contract:
 * - Server markup is always fully visible. Elements are hidden (armed) only
 *   AFTER hydration, and only when they are still comfortably below the
 *   viewport — so there is no flash, no hidden content for crawlers or no-JS
 *   visitors, and nothing above the fold ever animates on load.
 * - `data-reveal` reveals one element; `data-reveal-stagger` arms a group's
 *   direct children and reveals them with a 70ms cascade (capped at 8 steps).
 * - Reveals play once per page view; the observer disconnects on navigation
 *   and the next page is re-scanned via the pathname dependency.
 * - Bails out entirely under prefers-reduced-motion or without
 *   IntersectionObserver support.
 */
const ARMED = 'reveal-armed';
const SHOWN = 'reveal-in';

export default function RevealInit() {
  const pathname = usePathname();

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    const viewportH = window.innerHeight;

    /** Hide-and-observe only when the element is still below the fold. */
    const arm = (el: Element, delay = 0) => {
      if (el.classList.contains(SHOWN) || el.classList.contains(ARMED)) return false;
      if (el.getBoundingClientRect().top < viewportH * 0.85) return false;
      (el as HTMLElement).style.setProperty('--reveal-delay', `${delay}ms`);
      el.classList.add(ARMED);
      return true;
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target;
          if (el.hasAttribute('data-reveal-stagger')) {
            for (const child of el.children) child.classList.add(SHOWN);
          } else {
            el.classList.add(SHOWN);
          }
          io.unobserve(el);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -5% 0px' }
    );

    document.querySelectorAll('[data-reveal]').forEach((el) => {
      if (arm(el)) io.observe(el);
    });

    document.querySelectorAll('[data-reveal-stagger]').forEach((group) => {
      let armedAny = false;
      let i = 0;
      for (const child of group.children) {
        // Decorative children (e.g. the timeline rail) stay static.
        if (child.getAttribute('aria-hidden') === 'true') continue;
        if (arm(child, Math.min(i, 7) * 70)) armedAny = true;
        i += 1;
      }
      if (armedAny) io.observe(group);
    });

    return () => io.disconnect();
  }, [pathname]);

  return null;
}
